"use client";

import { useState, useEffect, useCallback, startTransition, useRef, type ReactNode } from "react";
import { fetchChoghadiya } from "@/lib/api/panchang";
import type { ChoghadiyaResponse } from "@/lib/api/panchang";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import {
  ChoghadiyaCitySelector,
  ChoghadiyaResult,
  formatDate,
  toDateString,
  type SavedCity,
} from "@/components/choghadiya";
import RatingPrompt from "@/components/RatingPrompt";

const STORAGE_KEY = "choghadiya_city";

type Language = "en" | "hi";

const COPY = {
  en: {
    kicker: "Aaj Ka Choghadiya",
    heading: "Today's Choghadiya",
    loadingLabel: "Loading today's Choghadiya",
    loadingBody: "Calculating day and night Choghadiya for your city",
    fetchError: "Failed to load Choghadiya",
    retry: "Try Again",
    trust: ["Accurate Vedic calculations", "Updated Daily", "100% Free"],
    rating: "Was today's Choghadiya accurate for your city?",
  },
  hi: {
    kicker: "Aaj Ka Choghadiya",
    heading: "Aaj Ka Choghadiya",
    loadingLabel: "Aaj ka Choghadiya load ho raha hai",
    loadingBody: "Aapke city ke din aur raat ka Choghadiya calculate ho raha hai",
    fetchError: "Choghadiya load nahi ho saka",
    retry: "Dobara try karein",
    trust: ["Sateek Vedic calculations", "Daily updated", "100% free"],
    rating: "Kya aaj ka Choghadiya aapke city ke liye sahi tha?",
  },
} as const;

const DEFAULT_CITY: SavedCity = {
  name: "New Delhi",
  latitude: 28.6139,
  longitude: 77.209,
};

function ChoghadiyaRating({ language }: { language: Language }) {
  return (
    <div style={{ marginTop: 24 }}>
      <RatingPrompt
        source={"tool_result" as never}
        mode="thumbs+text"
        prompt={COPY[language].rating}
        context={{
          surface: "choghadiya_result",
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }}
      />
    </div>
  );
}

function SkeletonBlock({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-md bg-stone-200/70 ${className}`} aria-hidden="true" />
  );
}

function ChoghadiyaResultSkeleton({ language }: { language: Language }) {
  const copy = COPY[language];
  const rows = Array.from({ length: 8 }, (_, index) => index);

  return (
    <div className="space-y-6" aria-label={copy.loadingLabel}>
      <div className="card-sacred p-6 sm:p-8">
        <div className="mb-6">
          <span className="block font-serif text-[32px] font-bold leading-tight text-ish-dark sm:text-[40px]">
            {copy.heading}
          </span>
          <span className="mt-2 block text-[14px] text-ish-text-muted">{copy.loadingBody}</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <SkeletonBlock className="mb-3 h-3 w-20" />
            <SkeletonBlock className="h-8 w-40" />
            <SkeletonBlock className="mt-2 h-3 w-32" />
          </div>
          <div>
            <SkeletonBlock className="mb-3 h-3 w-24" />
            <SkeletonBlock className="h-8 w-36" />
            <SkeletonBlock className="mt-2 h-3 w-28" />
          </div>
        </div>
      </div>

      <div className="card-sacred p-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {["good", "medium", "bad", "neutral"].map((item) => (
            <SkeletonBlock key={item} className="h-12" />
          ))}
        </div>
      </div>

      {["Day Choghadiya", "Night Choghadiya"].map((title) => (
        <div key={title} className="card-sacred overflow-hidden">
          <div className="border-b border-ish-border p-4">
            <SkeletonBlock className="h-5 w-36" />
            <SkeletonBlock className="mt-2 h-3 w-44" />
          </div>
          <div className="divide-y divide-ish-border">
            {rows.map((row) => (
              <div key={row} className="grid grid-cols-[1fr_auto] gap-3 p-3">
                <div>
                  <SkeletonBlock className="h-4 w-28" />
                  <SkeletonBlock className="mt-2 h-3 w-20" />
                </div>
                <SkeletonBlock className="h-8 w-24" />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="card-sacred p-4">
        <SkeletonBlock className="h-5 w-40" />
        <SkeletonBlock className="mt-3 h-3 w-full" />
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[0, 1, 2].map((item) => (
            <SkeletonBlock key={item} className="h-20" />
          ))}
        </div>
      </div>

      <div className="card-sacred p-6 text-center">
        <SkeletonBlock className="mx-auto h-5 w-48" />
        <SkeletonBlock className="mx-auto mt-3 h-3 w-64 max-w-full" />
        <SkeletonBlock className="mx-auto mt-5 h-11 w-44" />
      </div>
    </div>
  );
}

function getInitialCity(defaultCity?: SavedCity): SavedCity {
  if (defaultCity) return defaultCity;
  if (typeof window === "undefined") return DEFAULT_CITY;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULT_CITY;
    const parsed = JSON.parse(saved) as SavedCity;
    if (parsed.name && parsed.latitude && parsed.longitude) return parsed;
  } catch {
    // ignore
  }
  return DEFAULT_CITY;
}

function ChoghadiyaHeader({
  city,
  heading,
  language,
  todayFormatted,
  onCitySelect,
}: {
  city: SavedCity;
  heading?: string;
  language: Language;
  todayFormatted: string;
  onCitySelect: (city: SavedCity) => void;
}) {
  const copy = COPY[language];
  return (
    <div className="mb-10 text-center">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-ish-accent">
        {copy.kicker}
      </p>
      <h1 className="font-serif text-[36px] font-bold leading-tight text-ish-dark sm:text-[40px]">
        {heading ?? copy.heading}
      </h1>
      <p className="mt-2 text-[14px] text-ish-text-muted">{todayFormatted}</p>
      <ChoghadiyaCitySelector city={city} onCitySelect={onCitySelect} />
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium text-ish-text-faint">
        <TrustSignal icon="shield" label={copy.trust[0]} />
        <TrustSignal icon="clock" label={copy.trust[1]} />
        <TrustSignal icon="user" label={copy.trust[2]} />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" ? <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /> : null}
        {icon === "clock" ? (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        ) : null}
        {icon === "user" ? (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        ) : null}
      </svg>
      {label}
    </span>
  );
}

export default function ChoghadiyaClient({
  defaultCity,
  heading,
  initialDate,
  initialResult = null,
  language = "en",
  sourcePath,
  resultOffer,
  crossLinks,
}: {
  defaultCity?: SavedCity;
  heading?: string;
  initialDate?: string;
  initialResult?: ChoghadiyaResponse | null;
  language?: Language;
  sourcePath: string;
  resultOffer: ReactNode;
  crossLinks: ReactNode;
}) {
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ChoghadiyaResponse | null>(initialResult);
  const trackedInitialResult = useRef(false);
  const [city, setCity] = useState<SavedCity>(() => getInitialCity(defaultCity));
  const copy = COPY[language];

  const loadChoghadiya = useCallback(
    async (c: SavedCity) => {
      startTransition(() => {
        setLoading(true);
        setError(null);
      });
      try {
        const today = new Date();
        const response = await fetchChoghadiya({
          date: toDateString(today),
          latitude: c.latitude,
          longitude: c.longitude,
        });
        startTransition(() => setResult(response));
        trackCalculatorUsed({
          calculator: "choghadiya",
          language,
          source_path: sourcePath,
        });
      } catch (err) {
        startTransition(() => setError(err instanceof Error ? err.message : copy.fetchError));
      } finally {
        startTransition(() => setLoading(false));
      }
    },
    [copy.fetchError, language, sourcePath]
  );

  useEffect(() => {
    if (
      initialResult?.date === (initialDate ?? toDateString(new Date())) &&
      !trackedInitialResult.current
    ) {
      trackedInitialResult.current = true;
      trackCalculatorUsed({ calculator: "choghadiya", language, source_path: sourcePath });
      return;
    }
    loadChoghadiya(city);
  }, [city, initialDate, initialResult, language, loadChoghadiya, sourcePath]);

  function handleCitySelect(newCity: SavedCity) {
    setCity(newCity);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newCity));
    } catch {
      // ignore
    }
  }

  const todayFormatted = formatDate(new Date(), language === "hi" ? "hi-IN" : "en-IN");

  return (
    <div className="intent-interface-timing-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <ChoghadiyaHeader
          city={city}
          heading={heading}
          language={language}
          todayFormatted={todayFormatted}
          onCitySelect={handleCitySelect}
        />

        <div className="min-h-[980px]">
          {/* Loading State */}
          {loading && <ChoghadiyaResultSkeleton language={language} />}

          {/* Error State */}
          {error && !loading && (
            <div className="card-sacred p-6 text-center border-red-100 bg-red-50/50">
              <p className="text-[14px] text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => loadChoghadiya(city)}
                className="mt-3 text-[13px] font-semibold text-ish-accent"
              >
                {copy.retry}
              </button>
            </div>
          )}

          {/* Choghadiya Result */}
          {result && !loading && (
            <>
              <ChoghadiyaResult result={result} resultOffer={resultOffer} />
              <ChoghadiyaRating language={language} />
            </>
          )}
        </div>

        {crossLinks}
      </div>
    </div>
  );
}
